import { useEffect, useRef, useState } from 'react';
import ReactCodeInput from 'react-code-input';

import { faArrowRight, faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';
import { match } from 'ts-pattern';

import { useCountdownTimer } from 'lib/common/hooks/useCountdownTimer/useCountdownTimer';
import useIsSmallSoftphone from 'lib/common/hooks/useIsSmallSoftphone';
import useIsSoftphoneQuery from 'lib/common/hooks/useIsSoftphoneQuery';
import useTrigger from 'lib/common/hooks/useTrigger';

import Badge from 'lib/common/components/Badge';
import Button from 'lib/common/components/Button';
import Text from 'lib/common/components/Text';

import useIsMediumScreen from 'lib/common/mediaQueries/useIsMediumScreen';

import { MfaDestination, MfaDestinations } from '../../../types/AuthState';
import { reactCodeInputStyle, reactCodeInvalidStyle } from './EnterMFA.styles';
import { SelectMFA } from './SelectMFA';

import styles from './enter-mfa.module.scss';

const INPUT_QUERY_SELECTOR = '.react-code-input > input';

const ResendButton = ({
  setLoading,
  resendCode,
}: {
  setLoading: (value: boolean) => void;
  resendCode: () => Promise<void>;
}) => {
  const { value, restart, start } = useCountdownTimer(60);
  const [resendDisabled, setResendDisabled] = useState(true);

  useEffect(() => {
    start();
  }, []);

  useEffect(() => {
    if (value === 0) {
      setResendDisabled(false);
    }
  }, [value]);

  const onClickResend = async () => {
    if (resendDisabled) {
      return;
    }
    setResendDisabled(true);
    setLoading(true);
    await resendCode();
    setLoading(false);
    restart();
  };

  const resendText = value ? `Resend (${value}s)` : 'Resend';

  return (
    <Text
      inline
      type="link"
      disabled={resendDisabled}
      onClick={onClickResend}
      className={cx({ [styles['enter-mfa__resend--disabled']]: resendDisabled })}
      ariaRole={value ? 'timer' : 'link'}
    >
      {resendText}
    </Text>
  );
};

const Main = ({
  invalidCode,
  mfaSelection,
  mfaDestinations,
  provideMfaCode,
  openInfo,
  resendCode,
  restartAuthFlow,
  clearInvalidCode,
}: {
  invalidCode: boolean;
  mfaSelection: MfaDestination | undefined;
  mfaDestinations: { obfuscatedEmail: string | undefined; obfuscatedPhone: string | undefined };
  provideMfaCode: (code: string) => Promise<void>;
  openInfo: () => void;
  resendCode: () => Promise<void>;
  restartAuthFlow: () => Promise<void>;
  clearInvalidCode: () => void;
}) => {
  const isSoftphone = useIsSoftphoneQuery();
  const isSmallSoftphone = useIsSmallSoftphone();
  const isMediumScreen = useIsMediumScreen();

  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [trigger, changeTrigger] = useTrigger();

  const showMFASwitcher =
    mfaDestinations.obfuscatedEmail &&
    mfaDestinations.obfuscatedEmail !== '' &&
    mfaDestinations.obfuscatedPhone &&
    mfaDestinations.obfuscatedPhone !== '';

  // ReactCodeInput ref is a custom type that is not provided
  const inputRef = useRef<any>(null);
  const clickGoBack = async () => {
    setLoading(true);
    await restartAuthFlow();
  };

  useEffect(
    () =>
      // See NEON-4686 for context on this queueMicrotask call
      queueMicrotask(() => {
        const inputElements = document.querySelectorAll(INPUT_QUERY_SELECTOR);

        inputElements.forEach((element, index) => {
          element.ariaLabel =
            (index === 0 ? `${invalidCode ? 'Invalid' : 'Verification'} Code ` : '') + `Digit ${index + 1}`;
        });
      }),
    [invalidCode, loading]
  );

  useEffect(() => {
    if (code !== '') {
      clearInvalidCode();
    }
  }, [code]);

  return (
    <div className={cx(styles['enter-mfa'], { [styles['enter-mfa--softphone']]: isSoftphone })}>
      <Text type={'heading1'}>Just Confirming It's You</Text>
      <Text>
        We've{' '}
        {match(mfaSelection)
          .with(MfaDestinations.email, () => 'emailed')
          .with(MfaDestinations.sms, () => 'messaged')
          .with(undefined, () => 'sent')
          .exhaustive()}{' '}
        a verification code to{' '}
        {mfaSelection ? (
          <Badge
            type="SECONDARY"
            label={match(mfaSelection)
              .with(MfaDestinations.email, () => mfaDestinations.obfuscatedEmail)
              .with(MfaDestinations.sms, () => mfaDestinations.obfuscatedPhone)
              .exhaustive()}
          />
        ) : (
          'your device'
        )}
        .
        {match(mfaSelection)
          .with(MfaDestinations.email, () => <> Can't find the {mfaSelection}? Check your spam folder or </>)
          .with(MfaDestinations.sms, () => <> Not received the code? </>)
          .with(undefined, () => 'sent')
          .exhaustive()}
        <ResendButton resendCode={showMFASwitcher ? resendCode : restartAuthFlow} setLoading={setLoading} />.
      </Text>

      <form
        style={{ display: 'contents' }}
        onSubmit={async (e) => {
          e.preventDefault();

          setLoading(true);

          try {
            await provideMfaCode(code);
          } catch {
            setCode('');
            setLoading(false);

            // horrible unforgivable jank but the best we can do with this code input component
            inputRef.current.state.input.forEach((input: string) => (input = ''));
            inputRef?.current?.textInput?.[0]?.focus?.();
            changeTrigger();
          }
        }}
      >
        <ReactCodeInput
          ref={inputRef}
          value={code}
          key={`codeInputInvalid_${trigger}`}
          disabled={loading}
          isValid={!invalidCode}
          name="verification-code"
          inputMode="numeric"
          className={styles['enter-mfa__code-input']}
          onChange={(value) => setCode(value)}
          fields={6}
          inputStyle={{ ...reactCodeInputStyle({ isSoftphone, isSmallSoftphone }) }}
          inputStyleInvalid={{ ...reactCodeInputStyle({ isSoftphone, isSmallSoftphone }), ...reactCodeInvalidStyle }}
        />

        {invalidCode && (
          <Text color="danger" ariaRole="status">
            That code doesn't look quite right, give it another go.
          </Text>
        )}

        <div className={styles['enter-mfa__buttons']}>
          {(isSoftphone || isMediumScreen) && (
            <Button styleType="SECONDARY" endIcon={faCircleQuestion} onClick={openInfo}>
              How It Works
            </Button>
          )}
          <Button busy={loading} type="submit" endIcon={faArrowRight} disabled={code.length != 6}>
            Continue
          </Button>

          {showMFASwitcher && (
            <div className={styles['enter-mfa__link-box']}>
              <Text type="link" onClick={clickGoBack}>
                Go Back
              </Text>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

const Info = SelectMFA.Info;

export const EnterMFA = { Main, Info };
